/**
 * api.ts — cửa máy chủ của màn "Phòng Kinh doanh" (QĐ 0483, CEO chốt 17a
 * 28/10/2026) và thẻ "Ghi công" trên chi tiết đơn. Cơ cấu chỉ ĐỌC (HRM làm
 * chủ, CEO 7b); máy chủ cắt phạm vi người xem, màn chỉ vẽ.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import type { components } from "@/lib/api/generated/schema";
import { serverErrorMessage } from "@/lib/api/error-message";

export type SalesOrgView = components["schemas"]["SalesOrgView"];
export type SalesUnit = components["schemas"]["SalesUnit"];
export type SalesMember = components["schemas"]["SalesMember"];
export type SalesRole = components["schemas"]["SalesRole"];
export type SalesTarget = components["schemas"]["SalesTarget"];
export type SalesKeyItem = components["schemas"]["SalesKeyItem"];
export type SalesKeyItemInput = components["schemas"]["SalesKeyItemInput"];
export type SalesGnpmProduct = components["schemas"]["SalesGnpmProduct"];
export type SalesQualityMark = components["schemas"]["SalesQualityMark"];
export type SalesQualityInput = components["schemas"]["SalesQualityInput"];
export type SalesQualityChecker = components["schemas"]["SalesQualityChecker"];
export type SalesQualitySurveyItem = components["schemas"]["SalesQualitySurveyItem"];
export type SalesQualityMilestone = components["schemas"]["SalesQualityMilestone"];
export type SalesCareMilestone = components["schemas"]["SalesCareMilestone"];
export type SalesOrderCare = components["schemas"]["SalesOrderCare"];
export type SalesCreditLine = components["schemas"]["SalesCreditLine"];
export type SalesCreditProposeInput = components["schemas"]["SalesCreditProposeInput"];
export type SalesCreditOverrideInput = components["schemas"]["SalesCreditOverrideInput"];
export type SalesGNPMOrder = components["schemas"]["SalesGNPMOrder"];
export type SalesGNPMResplitInput = components["schemas"]["SalesGNPMResplitInput"];
export type SalesOrderCredits = components["schemas"]["SalesOrderCredits"];
export type SalesPendingOrder = components["schemas"]["SalesPendingOrder"];
export type SalesTeamSummary = components["schemas"]["SalesTeamSummary"];
export type SalesTeamSquad = components["schemas"]["SalesTeamSquad"];
export type SalesTeamPerson = components["schemas"]["SalesTeamPerson"];
export type SalesTeamWarning = components["schemas"]["SalesTeamWarning"];
export type SalesTeamQuarter = components["schemas"]["SalesTeamQuarter"];
export type SalesTeamQuarterSquad = components["schemas"]["SalesTeamQuarterSquad"];
export type SalesTeamOverview = components["schemas"]["SalesTeamOverview"];
export type SalesTeamOverviewNode = components["schemas"]["SalesTeamOverviewNode"];
export type SalesTeamOverviewMetrics = components["schemas"]["SalesTeamOverviewMetrics"];
export type SalesTeamOverviewPerson = components["schemas"]["SalesTeamOverviewPerson"];
export type SalesChatMetrics = components["schemas"]["SalesChatMetrics"];
export type SalesChatNumbers = components["schemas"]["SalesChatNumbers"];
export type SalesChatPerson = SalesChatMetrics["people"][number];
export type SalesPancakeAccounts = components["schemas"]["SalesPancakeAccounts"];
export type SalesPancakeAccount = SalesPancakeAccounts["items"][number];

export const salesKeys = {
  org: ["sales-org"] as const,
  targets: (month: string) => ["sales-org", "targets", month] as const,
  keyItems: (month: string) => ["sales-org", "key-items", month] as const,
  gnpmProducts: ["sales-org", "gnpm-products"] as const,
  quality: (month: string) => ["sales-org", "quality", month] as const,
  checkers: ["sales-org", "quality-checkers"] as const,
  survey: (month: string) => ["sales-org", "quality", month, "survey"] as const,
  milestones: (month: string) => ["sales-org", "quality", month, "milestones"] as const,
  orderCare: (orderId: string) => ["sales-order-care", orderId] as const,
  pending: ["sales-credits", "pending"] as const,
  gnpm: (month: string) => ["sales-credits", "gnpm-orders", month] as const,
  order: (orderId: string) => ["sales-credits", orderId] as const,
  summary: (month: string) => ["sales-team-summary", month] as const,
  // Dưới khoá bảng tổng: mọi chỗ làm mới bảng tổng (mục tiêu, ghi công…) làm mới luôn thẻ này.
  overview: (month: string) => ["sales-team-summary", month, "overview"] as const,
  overviewPerson: (month: string, staffId: string) => ["sales-team-summary", month, "overview", staffId] as const,
  chat: ["sales-team-chat"] as const,
  chatMetrics: (from: string, to: string, brand: string) => ["sales-team-chat", "metrics", from, to, brand] as const,
  pancakeAccounts: ["sales-team-chat", "pancake-accounts"] as const,
};

export function useSalesOrg(enabled = true) {
  return useQuery({
    queryKey: salesKeys.org,
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-org");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được cơ cấu Phòng Kinh doanh"));
      return data;
    },
    staleTime: 60_000,
  });
}

export function useSalesTargets(month: string) {
  return useQuery({
    queryKey: salesKeys.targets(month),
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-org/targets", { params: { query: { month } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được mục tiêu tháng"));
      return data.items;
    },
  });
}

export function useSaveTargets(month: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: { squadId: string; targetVnd: number }[]) => {
      const { data, error } = await api.PUT("/sales-org/targets/{month}", {
        params: { path: { month } },
        body: { items },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lưu được mục tiêu"));
      return data.items;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.targets(month) });
      void qc.invalidateQueries({ queryKey: salesKeys.summary(month) });
    },
  });
}

export function useSalesKeyItems(month: string) {
  return useQuery({
    queryKey: salesKeys.keyItems(month),
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-org/key-items", { params: { query: { month } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được món trọng điểm"));
      return data.items;
    },
  });
}

/** Món GNPM (bảng món GNPM gửi sang + món đã từng bán) — gợi ý cho ô chọn món trọng điểm hệ GNPM. */
export function useSalesGnpmProducts(enabled = true) {
  return useQuery({
    queryKey: salesKeys.gnpmProducts,
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-org/gnpm-products");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách món GNPM"));
      return data.items;
    },
  });
}

export function useSaveKeyItems(month: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: SalesKeyItemInput[]) => {
      const { data, error } = await api.PUT("/sales-org/key-items/{month}", {
        params: { path: { month } },
        body: { items },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lưu được món trọng điểm"));
      return data.items;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.keyItems(month) });
      void qc.invalidateQueries({ queryKey: salesKeys.summary(month) });
    },
  });
}

export function useSalesQuality(month: string) {
  return useQuery({
    queryKey: salesKeys.quality(month),
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-org/quality", { params: { query: { month } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được chất lượng tháng"));
      return data.items;
    },
  });
}

export function useSaveQuality(month: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: SalesQualityInput[]) => {
      const { data, error } = await api.PUT("/sales-org/quality/{month}", {
        params: { path: { month } },
        body: { items },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lưu được chất lượng tháng"));
      return data.items;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.quality(month) });
      void qc.invalidateQueries({ queryKey: salesKeys.summary(month) });
    },
  });
}

export function useRevertQuality(month: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { squadId: string; group: "care" | "survey" }) => {
      const { data, error } = await api.POST("/sales-org/quality/{month}/revert", { params: { path: { month } }, body: input });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không trở về số máy được"));
      return data.items;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.quality(month) });
      void qc.invalidateQueries({ queryKey: salesKeys.summary(month) });
    },
  });
}

export function useQualitySurvey(month: string, enabled: boolean) {
  return useQuery({
    queryKey: salesKeys.survey(month),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-org/quality-survey", { params: { query: { month } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được khách cần hỏi"));
      return data.items;
    },
  });
}

export function useAnswerSurvey(month: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; answer: "satisfied" | "unsatisfied" | "unreachable" }) => {
      const { error } = await api.PUT("/sales-org/quality-survey/{id}", {
        params: { path: { id: input.id } },
        body: { answer: input.answer },
      });
      if (error) throw new Error(serverErrorMessage(error, "Không ghi được câu trả lời"));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.quality(month) });
      void qc.invalidateQueries({ queryKey: salesKeys.summary(month) });
    },
  });
}

export function useQualityMilestones(month: string, enabled: boolean) {
  return useQuery({
    queryKey: salesKeys.milestones(month),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-org/quality-milestones", { params: { query: { month } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được mốc chăm khách"));
      return data.items;
    },
  });
}

export function useRuleMilestone(month: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["SalesQualityRulingInput"]) => {
      const { error } = await api.PUT("/sales-org/quality-milestones/ruling", { body });
      if (error) throw new Error(serverErrorMessage(error, "Không ghi được phán quyết"));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.quality(month) });
      void qc.invalidateQueries({ queryKey: salesKeys.summary(month) });
    },
  });
}

export function useOrderCare(orderId: string) {
  return useQuery({
    queryKey: salesKeys.orderCare(orderId),
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/care-milestones", { params: { path: { id: orderId } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được mốc chăm khách"));
      return data;
    },
  });
}

export function useMarkCare(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { kind: "reminder" | "prepare" | "follow_up"; undo: boolean }) => {
      const params = { path: { id: orderId, kind: input.kind } };
      const { data, error } = input.undo
        ? await api.DELETE("/orders/{id}/care-milestones/{kind}", { params })
        : await api.PUT("/orders/{id}/care-milestones/{kind}", { params });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không ghi được mốc chăm khách"));
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(salesKeys.orderCare(orderId), data);
    },
  });
}

export function useQualityCheckers(enabled = true) {
  return useQuery({
    queryKey: salesKeys.checkers,
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-org/quality-checkers");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được người nhập chất lượng"));
      return data.items;
    },
  });
}

export function useSaveQualityCheckers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (staffIds: string[]) => {
      const { data, error } = await api.PUT("/sales-org/quality-checkers", { body: { staffIds } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lưu được người nhập chất lượng"));
      return data.items;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.checkers });
      void qc.invalidateQueries({ queryKey: salesKeys.org });
    },
  });
}

export function usePendingCredits(enabled = true) {
  return useQuery({
    queryKey: salesKeys.pending,
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-credits/pending");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được ghi công chờ duyệt"));
      return data.items;
    },
  });
}

/** Đơn GNPM của tháng người xem chia lại / xác nhận được (T4, CEO 1a). */
export function useGNPMOrders(month: string, enabled = true) {
  return useQuery({
    queryKey: salesKeys.gnpm(month),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-credits/gnpm-orders", { params: { query: { month } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được đơn GNPM của tháng"));
      return data.items;
    },
  });
}

/** Bộ chia lại đơn GNPM đổi ⇒ khối đơn GNPM của tháng và bảng tổng phải tải lại. */
function useInvalidateGNPM(month: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: salesKeys.gnpm(month) });
    void qc.invalidateQueries({ queryKey: ["sales-team-summary"] });
  };
}

export function useResplitGNPM(month: string, orderId: string) {
  const done = useInvalidateGNPM(month);
  return useMutation({
    mutationFn: async (body: SalesGNPMResplitInput) => {
      const { data, error } = await api.PUT("/sales-credits/gnpm-orders/{orderId}", {
        params: { path: { orderId } },
        body,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không chia lại được ghi công đơn GNPM"));
      return data;
    },
    onSuccess: done,
  });
}

export function useConfirmGNPM(month: string, orderId: string) {
  const done = useInvalidateGNPM(month);
  return useMutation({
    mutationFn: async (version: number) => {
      const { data, error } = await api.POST("/sales-credits/gnpm-orders/{orderId}/confirm", {
        params: { path: { orderId } },
        body: { version },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không xác nhận được bộ chia lại"));
      return data;
    },
    onSuccess: done,
  });
}

export function useSalesTeamSummary(month: string, enabled = true) {
  return useQuery({
    queryKey: salesKeys.summary(month),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-team/summary", { params: { query: { month } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được bảng tổng tháng"));
      return data;
    },
  });
}

/** Thẻ «Tình hình kinh doanh»: máy chủ tính và cắt phạm vi (4a, 9a), màn chỉ vẽ. */
export function useSalesTeamOverview(month: string) {
  return useQuery({
    queryKey: salesKeys.overview(month),
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-team/overview", { params: { query: { month } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được tình hình kinh doanh"));
      return data;
    },
  });
}

export function useSalesTeamOverviewPerson(month: string, staffId: string | null) {
  return useQuery({
    queryKey: salesKeys.overviewPerson(month, staffId ?? ""),
    enabled: !!staffId,
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-team/overview/people/{staffId}", {
        params: { path: { staffId: staffId ?? "" }, query: { month } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được số của người này"));
      return data;
    },
  });
}

/** Thẻ «Data & chốt»: máy chủ ghép số chat với đơn thật và cắt phạm vi, màn chỉ vẽ. */
export function useSalesChatMetrics(from: string, to: string, brand: string) {
  return useQuery({
    queryKey: salesKeys.chatMetrics(from, to, brand),
    enabled: !!from && !!to,
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-team/chat-metrics", {
        params: { query: { from, to, ...(brand ? { brand: brand as "GNCA" | "GNPF" | "GNAT" | "GNTR" | "GNPM" } : {}) } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được số data và chốt"));
      return data;
    },
  });
}

export function usePancakeAccounts() {
  return useQuery({
    queryKey: salesKeys.pancakeAccounts,
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-team/pancake-accounts");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách tài khoản Pancake"));
      return data;
    },
  });
}

type PancakeDoor = "propose" | "confirm" | "revoke";

export function usePancakeAccountAction(door: PancakeDoor) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { pancakeUid: string; staffId?: string }) => {
      const { error } = await api.POST(`/sales-team/pancake-accounts/${door}`, { body });
      if (error) throw new Error(serverErrorMessage(error, "Không lưu được tài khoản Pancake"));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: salesKeys.chat }),
  });
}

export function useOrderCredits(orderId: string, enabled = true) {
  return useQuery({
    queryKey: salesKeys.order(orderId),
    enabled: enabled && !!orderId,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/sales-credits", { params: { path: { id: orderId } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được ghi công của đơn"));
      return data;
    },
  });
}

/** Ghi công đổi ⇒ thẻ của đơn, danh sách chờ duyệt và bảng tổng đều phải tải lại. */
function useInvalidateCredits(orderId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: salesKeys.order(orderId) });
    void qc.invalidateQueries({ queryKey: salesKeys.pending });
    void qc.invalidateQueries({ queryKey: ["sales-team-summary"] });
  };
}

export function useProposeCredits(orderId: string) {
  const done = useInvalidateCredits(orderId);
  return useMutation({
    mutationFn: async (body: SalesCreditProposeInput) => {
      const { data, error } = await api.PUT("/orders/{id}/sales-credits", { params: { path: { id: orderId } }, body });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gửi được ghi công"));
      return data;
    },
    onSuccess: done,
  });
}

export function useConfirmCredits(orderId: string) {
  const done = useInvalidateCredits(orderId);
  return useMutation({
    mutationFn: async (version: number) => {
      const { data, error } = await api.POST("/orders/{id}/sales-credits/confirm", {
        params: { path: { id: orderId } },
        body: { version },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không xác nhận được ghi công"));
      return data;
    },
    onSuccess: done,
  });
}

/** Người duyệt sửa lần mua / sale chủ máy tra, kèm lý do (CEO 4a) — bộ mới vẫn chờ xác nhận. */
export function useOverrideLookup(orderId: string) {
  const done = useInvalidateCredits(orderId);
  return useMutation({
    mutationFn: async (body: SalesCreditOverrideInput) => {
      const { data, error } = await api.PUT("/orders/{id}/sales-credits/lookup", {
        params: { path: { id: orderId } },
        body,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không sửa được lần mua / sale chủ"));
      return data;
    },
    onSuccess: done,
  });
}

export function useForfeitCredit(orderId: string) {
  const done = useInvalidateCredits(orderId);
  return useMutation({
    mutationFn: async (body: { staffId: string; note: string }) => {
      const { data, error } = await api.POST("/orders/{id}/sales-credits/forfeit", {
        params: { path: { id: orderId } },
        body,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đánh dấu được"));
      return data;
    },
    onSuccess: done,
  });
}
